import { useEffect, useRef, type RefObject } from 'react'

/**
 * Focuses the first focusable form field inside `containerRef` whenever
 * the container becomes "active" — accordion section expanded, panel
 * unfolded, sheet just-mounted, etc.
 *
 * Why: operators expand a panel because they're about to type. Without
 * autofocus they have to take a second click on the first field; with
 * it, the keyboard is already there. Pulled out as a shared hook so
 * every collapsible primitive (AccordionContent, ConfigPanel, future
 * sheets) opts in with one line.
 *
 * Behaviour:
 *
 *   - Fires when `isActive` flips false → true (or on mount when
 *     `isActive` starts true, unless `skipInitialMount`).
 *   - One frame of `requestAnimationFrame` lets layout settle + any
 *     entry animation paint before focus moves — avoids the scroll-jump
 *     that happens when you `.focus()` mid-frame.
 *   - Skip if a field inside the container is ALREADY focused — covers
 *     the race where the operator clicked into a field while the panel
 *     was animating in.
 *   - Skip if there are no focusable inputs — silent no-op, never
 *     throws. Read-only panels (no inputs) are fine.
 *   - Text fields with a current value are also `.select()`ed so the
 *     operator can type to overwrite — small polish for edit-existing
 *     flows. Buttons / checkboxes / radios are NOT included (focus on
 *     them is jarring on accordion expand).
 *
 * @param skipInitialMount Set true for primitives whose body is
 *   ALWAYS mounted (e.g. ConfigPanel uses `inert` to hide without
 *   unmounting). Without this, autofocus would fire on page load
 *   whenever the panel happens to start expanded — surprising. With
 *   it, only USER-triggered expansions trigger focus.
 *
 *   Primitives whose body is mounted ONLY when expanded (Radix
 *   AccordionContent without `forceMount`) should leave the default
 *   `false` — every mount IS a user-triggered expansion.
 */
export function useAutoFocusFirstField(
  containerRef: RefObject<HTMLElement | null>,
  isActive: boolean,
  options: { skipInitialMount?: boolean } = {},
): void {
  const { skipInitialMount = false } = options
  const wasActiveRef = useRef(false)
  const isInitialMountRef = useRef(true)

  useEffect(() => {
    const isInitialMount = isInitialMountRef.current
    isInitialMountRef.current = false

    if (!isActive) {
      wasActiveRef.current = false
      return
    }
    // Already fired for this active stretch — don't refire on rerender
    // (e.g. parent state change while the panel is open).
    if (wasActiveRef.current) return
    wasActiveRef.current = true

    if (isInitialMount && skipInitialMount) return

    const el = containerRef.current
    if (!el) return

    let cancelled = false
    const raf = requestAnimationFrame(() => {
      if (cancelled) return
      // Operator may have clicked into a field while the open animation
      // was running — don't steal focus from them.
      if (el.contains(document.activeElement)) return
      const target = el.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)
      if (!target) return
      target.focus()
      if (shouldPreselect(target)) {
        ;(target as HTMLInputElement | HTMLTextAreaElement).select()
      }
    })
    return () => {
      cancelled = true
      cancelAnimationFrame(raf)
    }
  }, [isActive, skipInitialMount, containerRef])
}

// Buttons + radios + checkboxes are deliberately NOT included — focusing
// them on accordion expand is jarring (a Cobrar button suddenly gets the
// ring; a checkbox could be triggered by Space). Only typable surfaces.
const FOCUSABLE_SELECTOR = [
  'input:not([disabled])',
  ':not([type="hidden"])',
  ':not([type="button"])',
  ':not([type="submit"])',
  ':not([type="reset"])',
  ':not([type="checkbox"])',
  ':not([type="radio"])',
  ':not([readonly])',
].join('') +
  ', textarea:not([disabled]):not([readonly])' +
  ', select:not([disabled])' +
  ', [contenteditable="true"]'

function shouldPreselect(el: Element): boolean {
  if (el instanceof HTMLInputElement) {
    const t = el.type
    return (
      (t === 'text' ||
        t === 'search' ||
        t === 'email' ||
        t === 'tel' ||
        t === 'url' ||
        t === 'number' ||
        t === 'password') &&
      !!el.value
    )
  }
  if (el instanceof HTMLTextAreaElement) {
    return !!el.value
  }
  return false
}
